import { Autocomplete, Grid, Skeleton, TextField } from '@mui/material';
import { type HTMLAttributes, useEffect, useRef, useState } from 'react';

import { useIntersectionObserver } from '@/Utility/hooks';

import * as data from './googleFonts.json';

import { useCustomFontContext } from '@/Utility/CustomFontContext';
import { getCustomFontFamilies } from '@/Utility/customFontHelper';

// Cache to track which fonts have been loaded to avoid repeated DOM operations
const loadedFonts = new Set<string>();

const createFontFace = (familyName: string) => {
  // Check cache first to avoid unnecessary work
  if (loadedFonts.has(familyName)) {
    return;
  }

  const fontFamilies = getCustomFontFamilies();
  const fontFamily = fontFamilies[familyName];

  if (!fontFamily || !fontFamily.variants) return;

  // Create @font-face declarations for all variants of this family
  const fontFaces = fontFamily.variants
    .map((variant: { url: string; weight: number; style: string }) => {
      const format = variant.url.endsWith('.woff2')
        ? 'woff2'
        : variant.url.endsWith('.woff')
          ? 'woff'
          : variant.url.endsWith('.ttf')
            ? 'truetype'
            : 'opentype';

      return `
      @font-face {
        font-family: '${familyName}';
        src: url('${variant.url}') format('${format}');
        font-weight: ${variant.weight};
        font-style: ${variant.style};
        font-display: swap;
      }
    `;
    })
    .join('');

  // Only inject styles if not already present
  const existingStyle = document.getElementById(`custom-font-${familyName}`);
  if (!existingStyle) {
    const style = document.createElement('style');
    style.id = `custom-font-${familyName}`;
    style.textContent = fontFaces;
    document.head.appendChild(style);

    // Add to cache to prevent future re-injection
    loadedFonts.add(familyName);
  } else {
    // Style exists, add to cache
    loadedFonts.add(familyName);
  }
};

type OptionType = {
  option: string;
} & HTMLAttributes<HTMLLIElement>;

const loadFont = (font: string, cb: VoidFunction) => {
  const link = document.createElement('link') as HTMLLinkElement;
  const href = `https://fonts.googleapis.com/css?family=${font}&subset=latin,latin-ext`;
  link.setAttribute('href', href);
  link.setAttribute('rel', 'stylesheet');
  link.setAttribute('type', 'text/css');
  link.onload = cb;

  document.head.appendChild(link);
};

const OptionItem = ({ option, ...props }: OptionType) => {
  const [fontLoaded, setFontLoaded] = useState<boolean>(false);

  const elRef = useRef<HTMLLIElement>(null);
  const observer = useIntersectionObserver(elRef, {
    threshold: 0,
    rootMargin: '1000px',
    freezeOnceVisible: false,
    root: elRef.current?.parentElement,
  });

  useEffect(() => {
    // Check if it's a system font or custom font (not a Google font)
    if (!data.fonts.includes(option)) {
      setFontLoaded(true);
    } else if (observer?.isIntersecting && !fontLoaded) {
      loadFont(option, () => {
        setFontLoaded(true);
      });
    }
  }, [observer, option, fontLoaded]);

  return (
    <li ref={elRef} {...props} style={{ fontFamily: option }}>
      <Grid container alignItems="center">
        {fontLoaded ? (
          <Grid size={{ xs: 12 }}>{option}</Grid>
        ) : (
          <Skeleton variant="rounded" width={'100%'} height={25} />
        )}
      </Grid>
    </li>
  );
};

type Props = {
  font: string;
  onChange: (value: string | null) => void;
  onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

const FontPicker = ({ font, onChange, onBlur }: Props) => {
  const [customFontFamilies, setCustomFontFamilies] = useState<string[]>([]);
  const { refreshTrigger } = useCustomFontContext();

  // Function to refresh custom fonts
  const refreshCustomFonts = () => {
    const fontFamilies = getCustomFontFamilies();
    const familyNames = Object.keys(fontFamilies);
    setCustomFontFamilies(familyNames);
  };

  // Load custom fonts from storage and respond to context updates
  useEffect(() => {
    refreshCustomFonts();
  }, [refreshTrigger]);

  // Create combined fonts array with custom fonts at the top
  const fontsArray = ['Arial', 'system-ui', 'sans-serif', 'monospace', ...customFontFamilies, ...data.fonts];

  // Load custom font CSS when font is selected
  useEffect(() => {
    const fontFamilies = getCustomFontFamilies();
    if (fontFamilies[font]) {
      createFontFace(font);
      // Also trigger global font loading for immediate application
      import('@/Utility/initApp/globalFontLoader').then(({ loadCustomFontGlobally }) => {
        loadCustomFontGlobally(font);
      });
    }
  }, [font]);

  return (
    <Autocomplete
      autoComplete
      id="font-auto"
      includeInputInList
      filterSelectedOptions
      value={font}
      fullWidth
      options={fontsArray}
      onChange={(_event, value) => {
        onChange(value);
      }}
      renderInput={(params) => (
        <TextField {...params} label="Type and select a font" style={{ fontFamily: font }} onBlur={onBlur} />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        return <OptionItem key={key} {...otherProps} option={option} />;
      }}
    />
  );
};

export type { Props as FontPickerProps };
export default FontPicker;
